import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import Breadcrumbs from '~/components/Breadcrumbs';
import useDebounce from '~/hooks/useDebounce';
import CategoryHeader from '~/pages/categories/CategoryHeader';
import usePaginateCategoryPosts from '~/pages/categories/hooks/usePaginateCategoryPosts';
import getCardStyles from '~/pages/index/_utils/getCardStyles';
import NewsFeedPosts from '~/pages/index/NewsFeed/NewsFeedPosts';
import getPaginatedPosts from '~/services/api/posts/getPaginatedPosts.server';
import getPublication from '~/services/api/publication/getPublication.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getContentTagIds from '~/utils/getContentTagIds';
import getMergedMetaData from '~/utils/getMergedMetaData';
import getUnnormalizedCategory from '~/utils/getUnnormalizedCategory';

export const meta: MetaFunction<typeof loader> = ({ matches, data }) => {
  const publication = data?.publication;
  const title = data?.title;
  const description = data?.description;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !title,
    overrides: {
      title: `${title} | ${publication?.name}`,
      description: description,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': publication?.url,
            name: publication?.name,
          },
        },
      ],
    },
  });
};

export const loader: LoaderFunction = async ({ request, params }: any) => {
  const { session } = await loaderSession(request);
  const url = new URL(request.url);
  const category = params?.category || '';
  const categoryText = getUnnormalizedCategory(category);

  const publication = await getPublication(request, session);

  if (!publication.has_pages) {
    return redirect('/');
  }

  const contentTag = publication?.content_tags?.find((tag: any) => tag.display === categoryText);

  const contentTagIds = getContentTagIds([categoryText], publication.content_tags);
  const paginatedPosts = await getPaginatedPosts(request, session, contentTagIds);

  return json(
    {
      publication,
      paginatedPosts,
      requestUrl: url.href,
      contentTagIds,
      title: contentTag?.display,
      description: contentTag?.description,
      cardStyles: getCardStyles({
        webTheme: publication.web_template.web_theme,
      }),
    },
    {},
  );
};

export default function Category() {
  const { title, description, publication, paginatedPosts, cardStyles, contentTagIds } = useLoaderData<typeof loader>();

  const { isSearching } = useDebounce(500);

  const { isLoading, fetchPosts, postsToRender, showLoadMore } = usePaginateCategoryPosts({
    initialPosts: paginatedPosts.posts,
    initialPagination: paginatedPosts.pagination,
    contentTagIds: contentTagIds,
  });

  const breadCrumbs = [
    {
      text: publication?.name,
      href: '../',
    },
    {
      text: 'Topics',
      href: undefined,
    },
    {
      text: title,
      href: undefined,
    },
  ];

  return (
    <>
      <div className="mx-auto min-h-screen w-full max-w-6xl px-2 py-4">
        {publication.render_breadcrumbs && (
          <div className="mt-8 pb-6">
            <Breadcrumbs breadcrumbs={breadCrumbs} />
          </div>
        )}
        <div className="space-y-8">
          <CategoryHeader title={title} description={description} publication={publication} />
          <NewsFeedPosts
            publication={publication}
            posts={postsToRender}
            isSearching={isSearching}
            isLoading={isLoading}
            showLoadMore={showLoadMore}
            fetchPosts={fetchPosts}
            cardStyles={cardStyles}
          />
        </div>
      </div>
    </>
  );
}
